import { api } from "@/lib/api";

export type EntitlementDerivedState = "none" | "active" | "expired" | "revoked";

export interface PaidResourceListItem {
  id: string;
  title: string;
  description: string | null;
  type: string;
  price: number;
  currency: string;
  accessDurationDays: number | null;
  derivedState: EntitlementDerivedState;
}

export interface PaidResourceDetail extends PaidResourceListItem {
  entitlement: {
    status?: string;
    expiresAt?: string | null;
    invoiceId?: string | null;
    derivedState: EntitlementDerivedState;
  };
}

export interface PricingRuleInput {
  patronTypeId: string;
  price: number;
  currency?: string;
  accessDurationDays?: number | null;
}

export const paidAccessApi = {
  listResources: () =>
    api.get<PaidResourceListItem[]>("/paid-access/resources").then((r) => r.data),

  getResource: (id: string) =>
    api.get<PaidResourceDetail>(`/paid-access/resources/${id}`).then((r) => r.data),

  purchase: (id: string) =>
    api
      .post<{
        type: "free" | "paid";
        invoiceId?: string;
        authorizationUrl?: string;
        entitlementId?: string;
      }>(`/paid-access/resources/${id}/purchase`)
      .then((r) => r.data),

  download: async (id: string, filename: string) => {
    const response = await api.get(`/paid-access/resources/${id}/download`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename.endsWith(".pdf") ? filename : `${filename}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  getPricingRules: (digitalResourceId: string) =>
    api
      .get<
        Array<{
          patronTypeId: string;
          price: string;
          currency: string;
          accessDurationDays: number | null;
        }>
      >(`/paid-access/admin/resources/${digitalResourceId}/pricing`)
      .then((r) => r.data),

  savePricingRules: (digitalResourceId: string, rules: PricingRuleInput[]) =>
    api
      .put(`/paid-access/admin/resources/${digitalResourceId}/pricing`, { rules })
      .then((r) => r.data),

  listPurchases: (digitalResourceId?: string) =>
    api
      .get<
        Array<{
          id: string;
          status: string;
          amountDue: string;
          currency: string;
          paidAt: string | null;
          metadata: Record<string, unknown> | null;
          user: { id: string; email: string; firstName: string | null; lastName: string | null };
        }>
      >("/paid-access/admin/purchases", {
        params: digitalResourceId ? { digitalResourceId } : undefined,
      })
      .then((r) => r.data),

  revokeEntitlement: (entitlementId: string) =>
    api.patch(`/paid-access/admin/entitlements/${entitlementId}/revoke`).then((r) => r.data),

  listEntitlements: (digitalResourceId?: string) =>
    api
      .get<
        Array<{
          id: string;
          status: string;
          grantedAt: string;
          expiresAt: string | null;
          invoiceId: string | null;
          user: { id: string; email: string; firstName: string | null; lastName: string | null };
          digitalResource: { id: string; title: string };
        }>
      >("/paid-access/admin/entitlements", {
        params: digitalResourceId ? { digitalResourceId } : undefined,
      })
      .then((r) => r.data),
};
